import { useEffect, useState } from "react";
import { Link, useRouter, useRouterState } from "@tanstack/react-router";
import {
  Activity,
  Filter,
  Globe,
  ListTodo,
  Slash,
  LogOut,
  Menu,
  Sparkles,
  Target,
  Workflow,
} from "lucide-react";
import { logout } from "@/api/auth";
import { cn } from "@/lib/cn";
import { useQueryClient } from "@tanstack/react-query";
import { SystemStatsHeader } from "@/components/system-stats";
import { CloseSyncOverlay } from "@/components/close-sync-overlay";

const NAV: Array<{ to: string; label: string; icon: typeof Activity }> = [
  { to: "/runs", label: "Runs", icon: Activity },
  { to: "/domains", label: "Domains", icon: Globe },
  { to: "/checks", label: "Checks", icon: ListTodo },
  { to: "/actions", label: "Actions", icon: Workflow },
  { to: "/extraction", label: "Extraction", icon: Sparkles },
  { to: "/cohorts", label: "Cohorts", icon: Target },
  { to: "/ignore", label: "Ignore", icon: Slash },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const { location } = useRouterState();
  const router = useRouter();
  const qc = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const onLogout = async () => {
    await logout();
    qc.clear();
    router.navigate({ to: "/login" });
  };

  return (
    <div className="flex min-h-screen">
      {drawerOpen ? (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      ) : null}
      <aside
        className={cn(
          "w-56 shrink-0 border-r border-border bg-card flex flex-col",
          "fixed inset-y-0 left-0 z-50 transition-transform",
          drawerOpen ? "translate-x-0" : "-translate-x-full",
          "lg:static lg:translate-x-0 lg:transition-none",
        )}
      >
        <div className="px-4 py-4 border-b border-border">
          <div className="flex items-center gap-2 font-semibold tracking-tight">
            <Filter className="h-5 w-5 text-accent" />
            <span>Leadsniffer</span>
          </div>
        </div>
        <nav className="flex-1 px-2 py-3 space-y-0.5">
          {NAV.map(({ to, label, icon: Icon }) => {
            const active =
              location.pathname === to ||
              (to !== "/" && location.pathname.startsWith(to + "/")) ||
              (to !== "/" && location.pathname.startsWith(to) && location.pathname.length === to.length);
            return (
              <Link
                key={to}
                to={to}
                className={cn(
                  "flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-sm",
                  active
                    ? "bg-accent/15 text-accent"
                    : "text-fg/80 hover:bg-subtle hover:text-fg",
                )}
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </Link>
            );
          })}
        </nav>
        <button onClick={onLogout} className="m-2 btn-ghost text-sm justify-start">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="lg:hidden flex items-center gap-2 px-3 py-2 border-b border-border bg-card">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="p-1.5 rounded-md hover:bg-subtle"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2 font-semibold">
            <Filter className="h-4 w-4 text-accent" />
            <span className="text-sm">Leadsniffer</span>
          </div>
        </div>
        <SystemStatsHeader />
        <main className="flex-1 min-w-0">{children}</main>
      </div>
      <CloseSyncOverlay />
    </div>
  );
}
